import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { EProcessNames } from "api/enums";
import AccessProfileModel from "api/models/AccessProfileModel";
import { PageLoading } from "components/UI/Page";
import useValidations from "hooks/useValidations";
import { lang } from "i18nLang";
import { useEffect, useState } from "react";
import { AccessProfileStore } from "store";

const useStyles = makeStyles({
  titleContainer: { marginBottom: "1rem" },
  topMargin: { marginTop: "1.25rem" },
  button: { marginLeft: "0.5rem" },
  loading: {
    position: "absolute",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    height: "calc(100vh - 128px)",
    top: 128,
    left: 0,
    zIndex: 2,
  },
});

const AccessProfileForm = ({
  selectedProfile,
  onClose,
}: {
  selectedProfile: AccessProfileModel | null;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const [profileName, setProfileName] = useState("");
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const isAdmin = selectedProfile?.isAdmin() || false;
  const processesConfig = Object.values(EProcessNames).map((key) => ({
    dataKey: key,
    label: lang(`userProfile.processes.${key}`),
  }));
  const { validated, validateFields, hasErrors } = useValidations({
    profileName: {
      required: true,
      custom: (value) =>
        AccessProfileStore.validateProfileName(
          value,
          selectedProfile?.uid || ""
        ),
    },
  });

  useEffect(() => {
    setProfileName(selectedProfile?.name || "");
    setPermissions(createPermissionsObj(selectedProfile));
  }, [selectedProfile]);

  const handleChangeInput = (ev) => {
    const { value } = ev.target;
    setProfileName(value);
  };

  const handleBlurInput = (ev) => {
    const { name, value } = ev.target;
    validateFields({ [name]: value });
  };

  const handleChangeCheckbox = (ev, checked: boolean) => {
    const { name } = ev.target;
    const newPermissions = { ...permissions };
    newPermissions[name] = checked;
    setPermissions(newPermissions);
  };

  const handleClickSave = () => {
    const permissionsList = Object.keys(permissions).filter(
      (key) => permissions[key]
    );

    setLoading(true);
    AccessProfileStore.saveAccessProfile(
      selectedProfile?.uid || "",
      profileName,
      permissionsList
    )
      .then(() => onClose())
      .finally(() => setLoading(false));
  };

  const handleClickDelete = () => {
    if (!selectedProfile) return;

    setLoading(true);
    AccessProfileStore.deleteAccessProfile(selectedProfile)
      .then((deleted) => {
        if (deleted) onClose();
      })
      .finally(() => setLoading(false));
  };

  function createPermissionsObj(profile: AccessProfileModel | null) {
    let newPermissionsObj = {};
    let permissions = profile?.isAdmin()
      ? Object.values(EProcessNames)
      : profile?.permissions || [];
    permissions.forEach((permission) => {
      newPermissionsObj[permission] = true;
    });
    return newPermissionsObj;
  }

  return (
    <Grid container item xs={6}>
      {loading && <PageLoading className={classes.loading} />}
      <Grid
        container
        item
        xs={12}
        className={classes.titleContainer}
        justifyContent="flex-end"
      >
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Grid>
      <Grid container item xs={12}>
        <TextField
          label={lang("profile")}
          name="profileName"
          value={profileName}
          fullWidth
          disabled={isAdmin}
          onChange={handleChangeInput}
          onBlur={handleBlurInput}
          error={!validated.profileName.valid}
          helperText={validated.profileName.text}
        />
        <Grid container className={classes.topMargin}>
          <Grid container item xs={6} direction="column">
            {processesConfig.slice(0, 6).map((process) => (
              <FormControlLabel
                key={process.dataKey}
                label={process.label}
                control={
                  <Checkbox
                    name={process.dataKey}
                    color="primary"
                    checked={permissions[process.dataKey] || false}
                    disabled={isAdmin}
                    onChange={handleChangeCheckbox}
                  />
                }
              />
            ))}
          </Grid>
          <Grid container item xs={6} direction="column">
            {processesConfig.slice(6).map((process) => (
              <FormControlLabel
                key={process.dataKey}
                label={process.label}
                control={
                  <Checkbox
                    name={process.dataKey}
                    color="primary"
                    checked={permissions[process.dataKey] || false}
                    disabled={isAdmin}
                    onChange={handleChangeCheckbox}
                  />
                }
              />
            ))}
          </Grid>
        </Grid>
        <Grid container className={classes.topMargin} justifyContent="flex-end">
          {selectedProfile && (
            <Button
              color="primary"
              disabled={isAdmin}
              onClick={handleClickDelete}
            >
              {lang("delete")}
            </Button>
          )}
          {!selectedProfile && (
            <Button color="primary" onClick={onClose}>
              {lang("cancel")}
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={hasErrors() || !profileName || isAdmin}
            onClick={handleClickSave}
          >
            {lang(selectedProfile ? "save" : "userProfile.createProfile")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AccessProfileForm;
